import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiLinkedin, FiYoutube, FiInstagram } from 'react-icons/fi';
import { RiTwitterXLine } from 'react-icons/ri';

const FooterContainer = styled.footer`
  background: rgba(10, 15, 28, 0.95);
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  padding: 3rem 0 1rem;
  margin-top: 4rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FooterSection = styled.div`
  h3 {
    color: #00d4ff;
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  p, a {
    color: #b0b7c3;
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }

  a {
    display: block;
    transition: color 0.3s ease;
    
    &:hover {
      color: #00d4ff;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;

  a {
    width: 40px;
    height: 40px;
    background: rgba(0, 212, 255, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #00d4ff;
    font-size: 1.2rem;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 212, 255, 0.2);
      color: #00a8cc;
      transform: translateY(-2px);
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(0, 212, 255, 0.1);
  padding-top: 1rem;
  text-align: center;
  color: #8892a6;
  font-size: 0.9rem;

  p {
    margin: 0;
  }

  a {
    color: #00d4ff;
  }
`;

const FooterCredit = styled.div`
  text-align: center;
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
  
  a {
    color: #6b7280;
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s ease;
    
    &:hover {
      color: #00d4ff;
    }
    
    &:focus {
      outline: 2px solid #00d4ff;
      outline-offset: 2px;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-top: 0.5rem;
    padding-top: 0.4rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    margin-top: 0.4rem;
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;

  input {
    flex: 1;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 6px;
    color: #ffffff;
    
    &::placeholder {
      color: #8892a6;
    }
    
    &:focus {
      outline: none;
      border-color: #00d4ff;
      background: rgba(255, 255, 255, 0.1);
    }
  }

  button {
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #00d4ff, #0099cc);
    color: #0a0f1c;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, #00a8cc, #007799);
      transform: translateY(-1px);
    }
  }
`;

function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setMessage('');
    
    try {
      const response = await fetch('https://theenergymuseum.com/api/newsletter.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessage(data.message);
        setEmail(''); // Clear the form
      } else {
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setMessage('Unable to subscribe. Please check your connection and try again.');
      console.error('Newsletter subscription error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <h3>About The Energy Museum</h3>
            <p>
              Discover the fascinating world of energy through immersive digital exhibitions, 
              interactive experiences, and expert-led consultations. Join us in exploring 
              the science that powers our world.
            </p>
            <SocialLinks>
              <a href="https://x.com/energymuseum" title="X (formerly Twitter)" target="_blank" rel="noopener noreferrer">
                <RiTwitterXLine />
              </a>
              <a href="https://linkedin.com/company/energymuseum" title="LinkedIn" target="_blank" rel="noopener noreferrer">
                <FiLinkedin />
              </a>
              <a href="https://youtube.com/energymuseum" title="YouTube" target="_blank" rel="noopener noreferrer">
                <FiYoutube />
              </a>
              <a href="https://instagram.com/energymuseum" title="Instagram" target="_blank" rel="noopener noreferrer">
                <FiInstagram />
              </a>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <h3>Explore</h3>
            <Link to="/" onClick={() => window.scrollTo(0, 0)}>Home</Link>
            <Link to="/about" onClick={() => window.scrollTo(0, 0)}>About Us</Link>
            <Link to="/about" onClick={() => {
              setTimeout(() => {
                const element = document.getElementById('our-team');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>Our Team</Link>
            <Link to="/services" onClick={() => window.scrollTo(0, 0)}>Our Services</Link>
            <Link to="/experiences" onClick={() => window.scrollTo(0, 0)}>Our Experiences</Link>
          </FooterSection>

          <FooterSection>
            <h3>Support & Services</h3>
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>Contact Us</Link>
            <Link to="/services" onClick={() => {
              setTimeout(() => {
                const element = document.getElementById('pricing');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>Pricing</Link>
            <Link to="/services" onClick={() => {
              setTimeout(() => {
                const element = document.getElementById('how-it-works');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}>How It Works</Link>
            <Link to="/forum" onClick={() => window.scrollTo(0, 0)}>Community Forum</Link>
            <Link to="/donate" onClick={() => window.scrollTo(0, 0)}>Donate</Link>
          </FooterSection>

          <FooterSection>
            <h3>Stay Connected</h3>
            <p>Subscribe to our newsletter for the latest exhibitions and energy insights.</p>
            <NewsletterForm onSubmit={handleNewsletterSubmit}>
              <input 
                type="email" 
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required 
                disabled={isSubmitting}
              />
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </NewsletterForm>
            {message && (
              <p style={{ 
                color: message.includes('Thank you') ? '#00d4ff' : '#ff6b6b', 
                fontSize: '0.9rem', 
                marginTop: '0.5rem' 
              }}>
                {message}
              </p>
            )}
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <p>
            © 2025 The Energy Museum, Inc. All rights reserved. | 
            <a href="/privacy"> Privacy Policy</a> | 
            <a href="/terms"> Terms of Service</a>
          </p>
        </FooterBottom>
        
        <FooterCredit>
          <p>
            Crafted with ⚡ by{' '}
            <a 
              href="https://www.justinjramalho.com" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Visit Justin J Ramalho's website"
            >
              justinjramalho
            </a>
          </p>
        </FooterCredit>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;